<script lang="ts">
  import type { Flashcard } from '../types/flashcard';
  import {
    CheckCircle,
    CheckSquare,
    ChevronLeft,
    ChevronRight,
    XCircle,
    Eye,
    Loader2,
    Pencil,
    RotateCcw,
    Save,
    Shuffle,
    Sparkles,
    Trash2,
    Volume2,
    BookOpen,
  } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import Button from './ui/button.svelte';
  import Card from './ui/card.svelte';
  import { flashcardActions, selectedTargetLanguage, selectedCategory, filteredCategories } from '../stores/flashcards';
  import { speakFlashcard, ttsService } from '../lib/tts';
  import { progressActions } from '../stores/progress';
  import { XPService } from '../lib/xp-service';
  import { supabase } from '../lib/supabase';
  import { userSettings } from '../stores/settings';
  import { onMount, onDestroy } from 'svelte';
  import { resolveApiUrl } from '../lib/utils';
  import { navigationActions } from '../stores/navigation';
  import { successConfetti } from '../lib/confetti';
  import { errorVibration, successVibration } from '../lib/animations';

  let { flashcardList, startIndex, onBack, onEdit }: { flashcardList: Flashcard[], startIndex?: number, onBack: () => void, onEdit?: (id: string) => void } = $props();

  // Test mode state
  let testMode = $state(false);
  let cardScores = $state<Map<string, number>>(new Map());
  let touchStartX = $state(0);
  let touchStartY = $state(0);

  // Track session info
  let sessionId = $state<string | null>(null);
  let cardsFlipped = $state<Set<string>>(new Set());

  // Track which cards were opened in this session to award XP only once
  let openedCards = $state<Set<string>>(new Set());

  let isFlipping = $state(false);
  let isSpeaking = $state(false);
  
  // Track if we've applied the startIndex
  let startIndexApplied = $state(false);
  
  let session = $state({
    currentIndex: 0,
    showFront: true,
    shuffled: false,
    direction: 'primary-to-target' as 'primary-to-target' | 'target-to-primary'
  });
  
  // Apply startIndex when prop becomes available (reactive)
  $effect(() => {
    if (!startIndexApplied && startIndex !== undefined && startIndex >= 0 && flashcardList.length > 0) {
      console.log('[FlashcardViewer $effect] Applying startIndex:', startIndex, 'flashcardList.length:', flashcardList.length);
      if (startIndex < flashcardList.length) {
        session.currentIndex = startIndex;
      }
      startIndexApplied = true;
    }
  });

  // Store shuffled version of flashcards
  let shuffledList = $state<Flashcard[]>([]);

  // Use shuffled list if shuffled, otherwise original list
  let baseList = $derived(session.shuffled ? shuffledList : flashcardList);
  
  // Filter based on test mode - show only cards with score < 5
  let unlockedCards = $derived(
    testMode ? baseList.filter(card => (cardScores.get(card.id) ?? 0) < 5) : baseList
  );
  
  let activeList = $derived(unlockedCards);

  let currentCard = $derived(activeList[session.currentIndex]);
  let totalCards = $derived(activeList.length);
  
  let frontContent = $derived(
    session.direction === 'primary-to-target' ? currentCard?.primary : currentCard?.target
  );
  let backContent = $derived(
    session.direction === 'primary-to-target' ? currentCard?.target : currentCard?.primary  
  );
  let phoneticContent = $derived(
    session.direction !== 'primary-to-target' ? '' :
    !currentCard ? '' :
    ($userSettings?.show_ipa_phonetic ? currentCard.phonetic || '' : currentCard.transliteration || currentCard.phonetic || '')
  );
  let showExamplesPanel = $state(false);
  let generatedExamples = $state<string[]>([]);
  let isGeneratingExamples = $state(false);
  let isSavingExamples = $state(false);
  let isDeletingExamples = $state(false);
  let examplesError = $state<string | null>(null);
  let lastCardId = $state<string | null>(null);
  let shouldShowExamplesControls = $derived(Boolean(currentCard) && !session.showFront);
  let storedExamples = $derived(currentCard?.examples ?? []);
  let displayExamples = $derived(
    generatedExamples.length > 0 ? generatedExamples : storedExamples
  );
  let hasGeneratedExamples = $derived(generatedExamples.length > 0);
  let hasStoredExamples = $derived(storedExamples.length > 0);
  let swipeFeedback = $state<{ type: 'correct' | 'incorrect' } | null>(null);
  let swipeFeedbackTimer: ReturnType<typeof setTimeout> | null = null;

  function handleCardFlip() {
    isFlipping = true;
    setTimeout(() => {
      session.showFront = !session.showFront;
      if (!session.showFront && currentCard) {
        cardsFlipped.add(currentCard.id);
      }
      isFlipping = false;
    }, 300);
  }

  function showSwipeFeedback(type: 'correct' | 'incorrect') {
    swipeFeedback = { type };

    if (swipeFeedbackTimer) {
      clearTimeout(swipeFeedbackTimer);
    }

    swipeFeedbackTimer = setTimeout(() => {
      swipeFeedback = null;
    }, 900);

    if (type === 'correct') {
      successConfetti();
      successVibration();
    } else {
      errorVibration();
    }
  }

  function handleTestModeAnswer(isCorrect: boolean) {
    if (!currentCard) return;

    const currentScore = cardScores.get(currentCard.id) ?? 0;
    let newScore = isCorrect ? currentScore + 1 : currentScore - 1;

    showSwipeFeedback(isCorrect ? 'correct' : 'incorrect');
    
    // Clamp score between 0 and 5
    newScore = Math.max(0, Math.min(5, newScore));
    
    cardScores.set(currentCard.id, newScore);
    
    // Mark as learned if reached 5 points
    if (newScore === 5) {
      flashcardActions.toggleLearned(currentCard.id);
    }
    
    // Move to next card
    if (session.currentIndex < totalCards - 1) {
      session.currentIndex++;
      session.showFront = true;
      const nextCard = activeList[session.currentIndex];
      if (nextCard) {
        awardOpenCardXP(nextCard.id);
      }
    }
  }

  function handleSwipeStart(e: TouchEvent) {
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
  }

  function handleSwipeEnd(e: TouchEvent) {
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    
    const deltaX = touchEndX - touchStartX;
    const deltaY = touchEndY - touchStartY;
    
    // Only register horizontal swipes (require more horizontal than vertical movement)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 50) {
        // Swipe right = correct answer
        handleTestModeAnswer(true);
      } else if (deltaX < -50) {
        // Swipe left = incorrect answer
        handleTestModeAnswer(false);
      }
    }
  }

  function toggleTestMode() {
    testMode = !testMode;
    if (testMode) {
      // Reset scores when entering test mode
      cardScores.clear();
      session.currentIndex = 0;
      session.showFront = true;
    }
  }

  function toggleLearnedLocal(e: Event) {
    e.stopPropagation();
    // Derive the current card from the active list and session index
    const card = activeList[session.currentIndex];
    if (!card) return;
    flashcardActions.toggleLearned(card.id);
  }

  async function handleBack() {
    // End session and save to database
    if (sessionId) {
      const cardsStudied = cardsFlipped.size;
      // Count only cards that were flipped AND are marked as learned
      const cardsLearned = Array.from(cardsFlipped).filter(cardId => {
        const card = activeList.find(c => c.id === cardId);
        return card?.isLearned ?? false;
      }).length;
      await progressActions.endSession(sessionId, cardsStudied, cardsLearned);
      // Reload recent sessions to update Dashboard
      await progressActions.loadRecentSessions();
    }

    // Position is saved automatically in onDestroy, no need to save here

    // Call original callback
    onBack();
  }

  // Award XP for opening a card (only once per card per session)
  async function awardOpenCardXP(cardId: string) {
    if (openedCards.has(cardId)) return; // Already awarded for this card

    openedCards.add(cardId);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await XPService.addXP(user.id, 'open_card', { flashcard_id: cardId });
      }
    } catch (error) {
      console.error('Failed to award open card XP:', error);
    }
  }

  function handleNext() {
    if (!testMode && session.currentIndex < totalCards - 1) {
      session.currentIndex++;
      session.showFront = true;
      // Award XP for viewing new card
      const nextCard = activeList[session.currentIndex];
      if (nextCard) {
        awardOpenCardXP(nextCard.id);
      }
    }
  }

  function handlePrevious() {
    if (!testMode && session.currentIndex > 0) {
      session.currentIndex--;
      session.showFront = true;
    }
  }

  // Award XP for the first card when component mounts
  // Also start the learning session
  onMount(async () => {
    console.log('[FlashcardViewer onMount] startIndex:', startIndex, 'session.currentIndex:', session.currentIndex);

    // Start session
    sessionId = await progressActions.startSession(
      $selectedTargetLanguage?.id,
      $selectedCategory?.id,
      'flashcards'
    );

    if (currentCard) {
      awardOpenCardXP(currentCard.id);
    }
  });

  // Save position when component is destroyed (e.g., navigating away via BottomNavigation)
  onDestroy(() => {
    console.log('[FlashcardViewer onDestroy] Saving position:', session.currentIndex);
    navigationActions.saveFlashcardPosition(
      session.currentIndex,
      $selectedTargetLanguage?.id,
      $selectedCategory?.id
    );
  });

  /**
   * Fisher-Yates shuffle algorithm
   * Creates a new shuffled array without modifying the original
   */
  function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  function handleShuffle() {
    if (session.shuffled) {
      // Un-shuffle: go back to original order
      session.shuffled = false;
    } else {
      // Shuffle: create new shuffled list
      shuffledList = shuffleArray(flashcardList);
      session.shuffled = true;
    }
    session.currentIndex = 0;
    session.showFront = true;
  }

  async function handlePlayAudio() {
    if (!currentCard) return;

    // Cancel any ongoing speech
    if (isSpeaking) {
      ttsService.cancel();
      isSpeaking = false;
      return;
    }

    try {
      isSpeaking = true;

      // Determine what text to speak and in what language
      let textToSpeak: string;
      let languageCode: string;

      // Get primary language from user settings (default to 'cs')
      const primaryLanguage = $userSettings?.preferred_language || 'cs';

      if (session.showFront) {
        // Speaking front side
        if (session.direction === 'primary-to-target') {
          // Primary language (user's native language from settings)
          textToSpeak = currentCard.primary;
          languageCode = primaryLanguage;
        } else {
          // Target language
          textToSpeak = currentCard.target;
          languageCode = currentCard.targetLanguage.code;
        }
      } else {
        // Speaking back side
        if (session.direction === 'primary-to-target') {
          // Target language
          textToSpeak = currentCard.target;
          languageCode = currentCard.targetLanguage.code;
        } else {
          // Primary language
          textToSpeak = currentCard.primary;
          languageCode = primaryLanguage;
        }
      }

      await speakFlashcard(textToSpeak, languageCode);
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      isSpeaking = false;
    }
  }

  function toggleDirection() {
    session.direction = session.direction === 'primary-to-target' ? 'target-to-primary' : 'primary-to-target';
    session.showFront = true;
  }

  async function handleGenerateUsageExamples() {
    if (!currentCard) return;

    isGeneratingExamples = true;
    examplesError = null;

    try {
      const primaryLanguage = $userSettings?.preferred_language || 'cs';
      const response = await fetch(
        resolveApiUrl('/api/example-usage'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            flashcardId: currentCard.id,
            primary: currentCard.primary,
            target: currentCard.target,
            primaryLanguageCode: primaryLanguage,
            targetLanguageCode: currentCard.targetLanguage.code,
          }),
        }
      );

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        const message =
          (payload && (payload.error || payload.details)) ||
          'Nepodařilo se vygenerovat příklady použití.';
        throw new Error(message);
      }

      if (!payload || !Array.isArray(payload.sentences)) {
        throw new Error('Odpověď AI neobsahuje žádné věty.');
      }

      generatedExamples = payload.sentences;
      showExamplesPanel = true;
    } catch (error) {
      console.error('Failed to generate example usage:', error);
      examplesError =
        error instanceof Error
          ? error.message
          : 'Nepodařilo se vygenerovat příklady použití.';
    } finally {
      isGeneratingExamples = false;
    }
  }

  async function handleSaveGeneratedExamples() {
    if (!currentCard || generatedExamples.length === 0) return;

    isSavingExamples = true;
    examplesError = null;

    try {
      await flashcardActions.updateFlashcard(currentCard.id, {
        examples: [...generatedExamples],
      });
      generatedExamples = [];
      showExamplesPanel = true;
    } catch (error) {
      console.error('Failed to save examples:', error);
      examplesError =
        error instanceof Error ? error.message : 'Uložení příkladů selhalo.';
    } finally {
      isSavingExamples = false;
    }
  }

  async function handleDeleteExamples() {
    if (!currentCard) return;

    isDeletingExamples = true;
    examplesError = null;

    try {
      await flashcardActions.updateFlashcard(currentCard.id, { examples: [] });
      generatedExamples = [];
      showExamplesPanel = false;
    } catch (error) {
      console.error('Failed to delete examples:', error);
      examplesError =
        error instanceof Error ? error.message : 'Mazání příkladů selhalo.';
    } finally {
      isDeletingExamples = false;
    }
  }

  $effect(() => {
    const cardId = currentCard?.id ?? null;

    if (cardId !== lastCardId) {
      lastCardId = cardId;
      generatedExamples = [];
      showExamplesPanel = false;
      examplesError = null;
    }
  });

  $effect(() => {
    if (session.showFront) {
      showExamplesPanel = false;
    }
  });
</script>

<div class="h-full safe-top flex flex-col overflow-hidden">
  <div class="container mx-auto px-4 py-2 pb-16 max-w-md flex flex-col flex-1 overflow-hidden">
    <!-- Header -->
    <div class="mb-2 flex-shrink-0">
      <h1 class="text-xl font-bold text-center mb-2">{$_('flashcards.viewer.flashcards_title')}</h1>
     
      <!-- Category Filter -->
      {#if $filteredCategories.length > 0}
        <div class="mb-2 space-y-1">
          <label for="viewer-category" class="form-label">{$_('flashcards.viewer.filter_category')}</label>
          <select
            id="viewer-category"
            bind:value={$selectedCategory}
            class="form-select w-full mt-2"
          >
            <option value={null}>{$_('flashcards.viewer.all_cards')}</option>
            {#each $filteredCategories as category (category.id)}
              <option value={category}>
                {category.name}
              </option>
            {/each}
          </select>
        </div>
      {/if}
    </div>

    <div class="flex mb-2 items-center justify-center gap-2 flex-shrink-0">
        <Button
          onclick={toggleDirection}
          variant="outline"
          size="sm"
          class="flex items-center gap-2"
        >
          <RotateCcw class="w-4 h-4" />
          {session.direction === 'primary-to-target' ? $_('flashcards.direction.primary_to_target') : $_('flashcards.direction.target_to_primary')}
        </Button>
        
        <Button
          onclick={handleShuffle}
          variant="outline"
          size="sm"
          class="flex items-center gap-2"
        >
          <Shuffle class="w-4 h-4" />
            {session.shuffled ? $_('buttons.shuffle') : $_('buttons.in_row')}
        </Button>

        <Button
          onclick={toggleTestMode}
          variant="outline"
          size="sm"
          class="flex items-center gap-2"
        >
          {#if testMode}
            <CheckSquare class="w-4 h-4" />
            {$_('buttons.test_mode')}
          {:else}
            <BookOpen class="w-4 h-4" />
            {$_('buttons.learn_mode')}
          {/if}
        </Button>
      </div>

    {#if !currentCard}
      <!-- No cards available -->
      <Card class="widget-card tone-header p-6 text-center flex-1">
        <p>{$_('messages.no_cards')}</p>
      </Card>
    {:else}
      <!-- Main flashcard -->
      <div 
        class="relative cursor-pointer mb-2 min-h-0 max-h-full {isFlipping ? 'animate-flip' : ''}"
        onclick={handleCardFlip}
        onkeydown={(e: KeyboardEvent) => e.key === 'Enter' || e.key === ' ' ? handleCardFlip() : null}
        ontouchstart={handleSwipeStart}
        ontouchend={handleSwipeEnd}
        role="button"
        tabindex="0"
      >
        <Card class="widget-surface tone-header h-64 p-3 flex items-center justify-center overflow-hidden">
          {#if swipeFeedback}
            <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div
                class={`flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-soft-md backdrop-blur-sm ${
                  swipeFeedback.type === 'correct'
                    ? 'bg-emerald-50/80 border-emerald-200 text-emerald-700 animate-bounce-in'
                    : 'bg-destructive/10 border-destructive/20 text-destructive-600 animate-shake'
                }`}
                aria-live="polite"
              >
                {#if swipeFeedback.type === 'correct'}
                  <CheckCircle class="w-6 h-6" />
                  <span class="font-semibold">{$_('flashcards.viewer.test_feedback_correct')}</span>
                {:else}
                  <XCircle class="w-6 h-6" />
                  <span class="font-semibold">{$_('flashcards.viewer.test_feedback_incorrect')}</span>
                {/if}
              </div>
            </div>
          {/if}
          <!-- Learned toggle (top-left) - hidden in test mode -->
          {#if !testMode}
            <button
              type="button"
              onclick={toggleLearnedLocal}
              aria-label={$_('aria.toggle_learned')}
              class="absolute top-4 left-4 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            >
              {#if currentCard?.isLearned}
                <span class="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-soft-sm">
                  <CheckCircle class="w-4 h-4" />
                </span>
              {:else}
                <span class="w-8 h-8 rounded-full flex items-center justify-center shadow-soft-sm">
                  <CheckCircle class="w-4 h-4 opacity-60" />
                </span>
              {/if}
            </button>
          {:else}
            <!-- Score display in test mode (top-left) -->
            <div class="absolute top-4 left-4 flex items-center gap-2">
              <div class="flex gap-1">
                {#each Array(5) as _, i}
                  <div 
                    class="w-3 h-3 rounded-full transition-all {i < (cardScores.get(currentCard?.id ?? '') ?? 0) ? 'bg-primary' : 'bg-gray-300'}"
                  ></div>
                {/each}
              </div>
            </div>
          {/if}
          
          <!-- Edit button - top-right -->
          {#if onEdit && currentCard && !testMode}
            <Button
              onclick={(e) => { e.stopPropagation(); onEdit(currentCard.id); }}
              variant="ghost"
              size="icon"
              class="absolute top-4 right-16 text-primary hover:text-primary/80"
              aria-label={$_('buttons.edit')}
            >
              <Pencil class="w-4 h-4" />
            </Button>
          {/if}

          <!-- Audio button - TTS for current text -->
          <Button
            onclick={(e) => { e.stopPropagation(); handlePlayAudio(); }}
            variant="ghost"
            size="icon"
            class="absolute top-4 right-4 text-primary {isSpeaking ? 'bg-white/80 animate-pulse rounded-full' : ''}"
            aria-label={isSpeaking ? $_('aria.stop_audio') : $_('aria.read_aloud')}
          >
            <Volume2 class="w-4 h-4 {isSpeaking ? 'text-primary' : ''}" />
          </Button>
          
          <div class="text-center">
            {#if session.showFront}
              <p class="text-2xl font-bold mb-2">
                {frontContent}
              </p>
            {:else}
              <p class="text-2xl font-bold mb-1">
                {backContent}
              </p>
              {#if phoneticContent}
                <p class="text-lg italic">
                  {phoneticContent}
                </p>
              {/if}
            {/if}
          </div>
        </Card>
      </div>

      <!-- Navigation - directly below flashcard -->
      {#if !testMode}
        <div class="flex items-center justify-between gap-2 mt-1 mb-2 flex-shrink-0">
          <Button
            onclick={handlePrevious}
            disabled={session.currentIndex === 0}
            variant="secondary"
            size="sm"
            class="flex items-center gap-2"
          >
            <ChevronLeft class="w-4 h-4" />
            {$_('buttons.previous')}
          </Button>

          <div class="rounded-full widget-muted px-3 py-1.5">
            <span class="text-xs font-medium">
              {session.currentIndex + 1} / {totalCards}
            </span>
          </div>

          <Button
            onclick={handleNext}
            disabled={session.currentIndex === totalCards - 1}
            variant="secondary"
            size="sm"
            class="flex items-center gap-2"
          >
            {$_('buttons.next')}
            <ChevronRight class="w-4 h-4" />
          </Button>
        </div>
      {:else}
        <!-- Test mode: show progress bar -->
        {#if totalCards > 0}
          <div class="mt-1 mb-2 text-center flex-shrink-0">
            <div class="rounded-full widget-muted px-3 py-1.5 inline-block">
              <span class="text-xs font-medium">
                {session.currentIndex + 1} / {totalCards}
              </span>
            </div>
          </div>
        {/if}
      {/if}

      <!-- Test mode instructions under flashcard -->
      {#if testMode && currentCard && !shouldShowExamplesControls}
        <div class="mb-3 px-4 py-2 rounded-lg bg-primary/10 text-center flex-shrink-0">
          <p class="text-sm text-foreground">
            {$_('messages.test_mode_instructions')}
          </p>
        </div>
      {/if}


      <!-- Bottom controls -->
      {#if testMode && totalCards === 0}
        <Card class="widget-card tone-header p-6 text-center flex-1">
          <p class="text-lg font-semibold">{$_('messages.test_mode_no_cards')}</p>
        </Card>
      {:else if shouldShowExamplesControls && !testMode}
        <!-- Edit mode: always show examples (target side) -->
        <div class="mb-1 w-full text-left flex-shrink-0">
          <p class="text-xs font-semibold uppercase tracking-wide">
            Příklad použití ve větách
          </p>

          <div class="mt-1 flex flex-wrap gap-2">
          {#if displayExamples.length > 0}
            <Button
              onclick={() => {
                showExamplesPanel = !showExamplesPanel;
                examplesError = null;
              }}
              variant="secondary"
              size="sm"
              disabled={isGeneratingExamples}
              class="flex items-center gap-2"
            >
              {#if isGeneratingExamples}
                <Loader2 class="w-4 h-4 animate-spin" />
              {:else}
                <Sparkles class="w-4 h-4" />
              {/if}
              Aktualizovat příklady
            </Button>
          {:else}
            <Button
              onclick={handleGenerateUsageExamples}
              variant="secondary"
              size="sm"
              disabled={isGeneratingExamples}
              class="flex items-center gap-2"
            >
              {#if isGeneratingExamples}
                <Loader2 class="w-4 h-4 animate-spin" />
              {:else}
                <Sparkles class="w-4 h-4" />
              {/if}
              Vygenerovat příklady
            </Button>
          {/if}

            {#if hasGeneratedExamples}
              <Button
                onclick={handleSaveGeneratedExamples}
                variant="secondary"
                size="sm"
                disabled={isSavingExamples}
                class="flex items-center gap-2"
              >
                {#if isSavingExamples}
                  <Loader2 class="w-4 h-4 animate-spin" />
                {:else}
                  <Save class="w-4 h-4" />
                {/if}
                {$_('buttons.save')}
              </Button>
            {/if}

            {#if hasStoredExamples}
              <Button
                onclick={handleDeleteExamples}
                variant="ghost"
                size="sm"
                disabled={isDeletingExamples}
                class="flex items-center gap-2 text-destructive hover:text-destructive/80"
              >
                {#if isDeletingExamples}
                  <Loader2 class="w-4 h-4 animate-spin" />
                {:else}
                  <Trash2 class="w-4 h-4" />
                {/if}
                {$_('buttons.delete')}
              </Button>
            {/if}
          </div>

          {#if displayExamples.length > 0 }
            <Card class="mt-2 widget-muted p-3 space-y-2 overflow-y-auto max-h-32">
                <ul class="space-y-1.5 text-sm">
                  {#each displayExamples as sentence, index}
                    <li class="flex items-start gap-2">
                      <span class="text-xs font-semibold pt-0.5 text-primary">{index + 1}.</span>
                      <span>{sentence}</span>
                    </li>
                  {/each}
                </ul>
            </Card>
          {/if}

          {#if examplesError}
            <p class="mt-2 text-sm text-destructive">{examplesError}</p>
          {/if}
        </div>
      {:else if testMode && shouldShowExamplesControls && storedExamples.length > 0}
        <!-- Test mode: show examples with toggle button (target side only) -->
        <div class="mb-1 w-full text-left flex-shrink-0">
          <p class="text-xs font-semibold uppercase tracking-wide">
            Příklad použití ve větách
          </p>

          <div class="mt-1 flex flex-wrap gap-2">
            <Button
              onclick={() => {
                showExamplesPanel = !showExamplesPanel;
              }}
              variant="secondary"
              size="sm"
              class="flex items-center gap-2"
            >
              <Eye class="w-4 h-4" />
              { showExamplesPanel ? 'Skrýt' : 'Zobrazit' }
            </Button>
          </div>

          {#if showExamplesPanel}
            <Card class="mt-2 widget-muted p-3 space-y-2 overflow-y-auto max-h-32">
                <ul class="space-y-1.5 text-sm">
                  {#each storedExamples as sentence, index}
                    <li class="flex items-start gap-2">
                      <span class="text-xs font-semibold pt-0.5 text-primary">{index + 1}.</span>
                      <span>{sentence}</span>
                    </li>
                  {/each}
                </ul>
            </Card>
          {/if}
        </div>
      {/if}

 
    {/if}
  </div>
</div>
